import React, { useCallback, useMemo, useRef, useState } from "react";
import globalMU from "../../../globalMU";
import { IColuna } from "../../types/IColuna";
import Legenda from "../itens-orcamento/legenda/Legenda";
import { AcoesHeadEstoque } from "./TableHead/AcoesHeadEstoque";
import { HeadersEstoque } from "./TableHead/HeaderEstoque";
import { PagedMessageResponse } from "app/api/types/PagedMessageResponse";

const estiloEstoque = require("./estoque.module.scss").default;
import { fonteGradeEmPxOuPadrao } from 'app/utils/tamanhoFonteGrid'

const normalizarNomeColuna = (nomeColuna: string) => (nomeColuna ?? "").replace(/\s+/g, "_");

const LEGENDA_ITENS_ESTOQUE = [
  {
    nome: "Quantidade em Trânsito",
    cor: "darkgoldenrod",
  },
  {
    nome: "Quantidade Pedida aos Fornecedores",
    cor: "coral",
  },
  {
    nome: " Quantidade não Entregue \"Expedição\"",
    cor: "#f00",
  },
  {
    nome: " Quantidade não Entregue \"Futura\"",
    cor: "#fc0",
  },
  {
    nome: " Quantidade Disponível em Lote",
    cor: "#0a0",
  },
  {
    nome: " * Permite Entrega Futura Outro Local Direto para o Cliente de Venda de Outra Filial",
    cor: "#11aa99",
  },
  {
    nome: " * Permite Solicitação de Transferência de Outra Filial (na Venda)",
    cor: "#556b2f",
  },
];

export type EntitesDadosEstoque = {
  Codigo?: string;
  Descricao?: string;
  Filial?: string;
  PedidosFornecedor?: number;
  QtdeNaoEntregueExpedicao?: number;
  Quantidade?: number;
  QuantidadeDisponivel?: number;
  LocalFisico?: string;
  QuantidadeDisponivelLote?: number;
  QuantidadeEmTransito?: number;
  QuantidadeNaoEntregueFutura?: number;
  QuantidadeReservado?: number;
  QuantidadeRMA?: number;
  Encomenda?: number;
  QuantidadeMostruario?: number;
  ReservaMostruario?: number;
  R?: string;
  T?: string;
};

export type EstoqueProps = {
  produto: string;
  descricao: string;
  dadosEstoque: PagedMessageResponse<EntitesDadosEstoque>;
  colunas: IColuna[];
  tamanhoFontePadrao: number;
  corSelecionado?: string;
  textoSelecionado?: string;
  permiteEngrenagemGrid: boolean;
  estoqueSelecionado: EntitesDadosEstoque;
  onToggleColunaSelecionada: (nomeColuna: string, selecionada: boolean) => void;
  onToggleSelecionarTodasColunas: (selecionar: boolean) => void;
  onAlterarFonte: (tamanho: number) => void;
  onSelecionarDadoEstoque: (entitesDadosEstoque: EntitesDadosEstoque) => void;
  mostrarColunaEncomenda: boolean;
  mostrarColunaLocalFisico: boolean;
};

export const Estoque = React.memo((props: EstoqueProps) => {
  const [estoqueSelecionado, setEstoqueSelecionado] =
    useState<EntitesDadosEstoque>(props.estoqueSelecionado);

  const estoqueSelecionadoRef = useRef(estoqueSelecionado);
  estoqueSelecionadoRef.current = estoqueSelecionado;

  const descricaoProdutoSelecionado = useMemo(() => (
    <div className={estiloEstoque.InformacoesProduto}
      id="Estoque_descricaoProdutoSelecionado"
    >
      <div className={estiloEstoque.primeiraColuna}>Produto</div>
      <div style={{ marginTop: "7px" }}>{props.produto}</div>
      <div className={estiloEstoque.primeiraColuna}>Descrição</div>
      <div style={{ marginTop: "7px" }}>{props.descricao}</div>
    </div>
  ), [props.produto, props.descricao]);

  const colunasOcultas = useCallback((nomeColuna: string) => {
    if (nomeColuna == "Encomenda") return !props.mostrarColunaEncomenda;
    if (nomeColuna == "Local Físico") return !props.mostrarColunaLocalFisico;
    return false;
  }, [props.mostrarColunaEncomenda, props.mostrarColunaLocalFisico]);

  const colunasCabecalho = useMemo(
    () => props.colunas.filter((coluna) => coluna.Permitido !== false && !colunasOcultas(coluna.Nome)),
    [props.colunas, colunasOcultas]
  );

  const colunasSelecionadas = useMemo(
    () => props.colunas.filter((c) => c.Permitido !== false && c.Selecionada && !colunasOcultas(c.Nome)),
    [props.colunas, colunasOcultas]
  );

  const toggleEstoqueSelecionado = useCallback((dadosEstoque: EntitesDadosEstoque) => {
    if (estoqueSelecionadoRef.current == dadosEstoque) {
      setEstoqueSelecionado(null);
      props.onSelecionarDadoEstoque(null);
    } else {
      setEstoqueSelecionado(dadosEstoque);
      props.onSelecionarDadoEstoque(dadosEstoque);
    }
  }, [props.onSelecionarDadoEstoque]);

  const renderizarCelulaEstoque = useCallback(
    (nomeColuna: string, value: EntitesDadosEstoque, indiceLinha: number) => {
      const idColuna = `Estoque_Coluna_${normalizarNomeColuna(nomeColuna)}_${indiceLinha}`;

      switch (nomeColuna) {
        case "Filial":
          return <td id={idColuna} className={estiloEstoque.alinhadoDireita}>{value.Filial}</td>;
        case "Local Físico":
          return <td id={idColuna} className={estiloEstoque.alinhadoDireita}>{value.LocalFisico}</td>;
        case "Quantidade":
          return <td id={idColuna}>{globalMU.formatarValorNumerico(value.Quantidade, 3)}</td>;
        case "Reservado":
          return <td id={idColuna}>{globalMU.formatarValorNumerico(value.QuantidadeReservado, 3)}</td>;
        case "Disponível":
          return <td id={idColuna}>{globalMU.formatarValorNumerico(value.QuantidadeDisponivel, 3)}</td>;
        case "Qtde RMA":
        case "Quantidade RMA":
          return (
            <td id={idColuna}>
              {value.QuantidadeRMA != 0
                ? globalMU.formatarValorNumerico(value.QuantidadeRMA, 3, false)
                : null}
            </td>
          );
        case "Em Trânsito":
          return (
            <td id={idColuna} className={estiloEstoque.emTransito}>
              {value.QuantidadeEmTransito != 0
                ? globalMU.formatarValorNumerico(value.QuantidadeEmTransito, 3, false)
                : null}
            </td>
          );
        case "Pedido ao Fornecedor":
          return (
            <td id={idColuna} className={estiloEstoque.pedidosFornecedor}>
              {value.PedidosFornecedor != 0
                ? globalMU.formatarValorNumerico(value.PedidosFornecedor, 3, false)
                : null}
            </td>
          );
        case "Não Entregue Expedição":
          return (
            <td id={idColuna} className={estiloEstoque.qtdeEntregueExpedicao}>
              {value.QtdeNaoEntregueExpedicao != 0
                ? globalMU.formatarValorNumerico(value.QtdeNaoEntregueExpedicao, 3, false)
                : null}
            </td>
          );
        case "Não Entregue Futura":
          return (
            <td id={idColuna} className={estiloEstoque.quantidadeNEntregueFutura}>
              {value.QuantidadeNaoEntregueFutura != 0
                ? globalMU.formatarValorNumerico(value.QuantidadeNaoEntregueFutura, 3, false)
                : null}
            </td>
          );
        case "Disponível Lote":
          return (
            <td id={idColuna} className={estiloEstoque.disponivelLote}>
              {value.QuantidadeDisponivelLote != 0
                ? globalMU.formatarValorNumerico(value.QuantidadeDisponivelLote, 3, false)
                : null}
            </td>
          );
        case "Mostruário":
          return <td id={idColuna}>{globalMU.formatarValorNumerico(value.QuantidadeMostruario, 3)}</td>;
        case "Reserva Mostruário":
          return <td id={idColuna}>{globalMU.formatarValorNumerico(value.ReservaMostruario, 3)}</td>;
        case "R":
          return <td id={idColuna} className={estiloEstoque.R}>{value.R}</td>;
        case "T":
          return <td id={idColuna} className={estiloEstoque.T}>{value.T}</td>;
        case "Encomenda":
          return (
            <td id={idColuna}>
              {value.Encomenda != 0
                ? globalMU.formatarValorNumerico(value.Encomenda, 3, false)
                : null}
            </td>
          );
        default:
          return null;
      }
    },
    []
  );

  const linhasDados = useMemo(() => (
    props.dadosEstoque?.Entities?.map((value, index) => (
      <tr
        key={value.Filial ?? index}
        className={
          estoqueSelecionado?.Filial == value.Filial
            ? estiloEstoque.linhaSelecionada
            : estiloEstoque.linha
        }
        style={
          estoqueSelecionado?.Filial == value.Filial
            ? {
              backgroundColor: props?.corSelecionado,
              color: props?.textoSelecionado,
              fontSize: props.tamanhoFontePadrao,
            }
            : { fontSize: props.tamanhoFontePadrao }
        }
        onClick={() => {
          toggleEstoqueSelecionado(value);
        }}
      >
        {colunasSelecionadas.map((coluna) => (
          <React.Fragment key={coluna.Nome}>
            {renderizarCelulaEstoque(coluna.Nome, value, index)}
          </React.Fragment>
        ))}
      </tr>
    ))
  ), [
    props.dadosEstoque,
    colunasSelecionadas,
    estoqueSelecionado,
    props.corSelecionado,
    props.textoSelecionado,
    props.tamanhoFontePadrao,
    toggleEstoqueSelecionado,
    renderizarCelulaEstoque,
  ]);

  const totaisEstoque = useMemo(() => {
    const entidades = props.dadosEstoque?.Entities ?? [];

    let totalQuantidade = 0;
    let totalReservado = 0;
    let totalMostruario = 0;
    let totalReservaMostruario = 0;
    let totalDisponivel = 0;
    let totalTransito = 0;
    let totalPedidoFornecedor = 0;
    let totalNaoEntregueExpedicao = 0;
    let totalNaoEntregueFutura = 0;
    let totalDisponivelLote = 0;
    let totalEncomenda = 0;

    entidades.forEach((x) => {
      totalQuantidade += x.Quantidade;
      totalReservado += x.QuantidadeReservado;
      totalMostruario += x.QuantidadeMostruario ?? 0;
      totalReservaMostruario += x.ReservaMostruario ?? 0;
      totalDisponivel += x.QuantidadeDisponivel;
      totalTransito += x.QuantidadeEmTransito;
      totalPedidoFornecedor += x.PedidosFornecedor;
      totalNaoEntregueExpedicao += x.QtdeNaoEntregueExpedicao;
      totalNaoEntregueFutura += x.QuantidadeNaoEntregueFutura;
      totalDisponivelLote += x.QuantidadeDisponivelLote;
      totalEncomenda += x.Encomenda;
    });

    const totalQuantidadeRMA = entidades.reduce((t, x) => t + (x.QuantidadeRMA ?? 0), 0);

    return {
      Quantidade: totalQuantidade,
      Reservado: totalReservado,
      Mostruario: totalMostruario,
      ReservaMostruario: totalReservaMostruario,
      Disponivel: totalDisponivel,
      Transito: totalTransito,
      PedidoFornecedor: totalPedidoFornecedor,
      NaoEntregueExpedicao: totalNaoEntregueExpedicao,
      NaoEntregueFutura: totalNaoEntregueFutura,
      DisponivelLote: totalDisponivelLote,
      Encomenda: totalEncomenda,
      QuantidadeRMA: totalQuantidadeRMA,
    };
  }, [props.dadosEstoque]);

  const renderizarCelulaTotalEstoque = useCallback((nomeColuna: string) => {
    const idColuna = `Estoque_Total_${normalizarNomeColuna(nomeColuna)}`;

    switch (nomeColuna) {
      case "Filial":
        return <td id={idColuna} className={estiloEstoque.totalTabela}>Totais</td>;
      case "Local Físico": // 447170
        return props.mostrarColunaLocalFisico
          ? <td id={idColuna} className={estiloEstoque.totalTabela}></td>
          : null;
      case "Quantidade":
        return <td id={idColuna} className={estiloEstoque.totalTabela}>{globalMU.formatarValorNumerico(totaisEstoque.Quantidade, 3)}</td>;
      case "Reservado":
        return <td id={idColuna} className={estiloEstoque.totalTabela}>{globalMU.formatarValorNumerico(totaisEstoque.Reservado, 3)}</td>;
      case "Disponível":
        return <td id={idColuna} className={estiloEstoque.totalTabela}>{globalMU.formatarValorNumerico(totaisEstoque.Disponivel, 3)}</td>;
      case "Em Trânsito":
        return <td id={idColuna} className={estiloEstoque.totalTabela}>{globalMU.formatarValorNumerico(totaisEstoque.Transito, 3)}</td>;
      case "Pedido ao Fornecedor":
        return <td id={idColuna} className={estiloEstoque.totalTabela}>{globalMU.formatarValorNumerico(totaisEstoque.PedidoFornecedor, 3)}</td>;
      case "Não Entregue Expedição":
        return <td id={idColuna} className={estiloEstoque.totalTabela}>{globalMU.formatarValorNumerico(totaisEstoque.NaoEntregueExpedicao, 3)}</td>;
      case "Não Entregue Futura":
        return <td id={idColuna} className={estiloEstoque.totalTabela}>{globalMU.formatarValorNumerico(totaisEstoque.NaoEntregueFutura, 3)}</td>;
      case "Disponível Lote":
        return <td id={idColuna} className={estiloEstoque.totalTabela}>{globalMU.formatarValorNumerico(totaisEstoque.DisponivelLote, 3)}</td>;
      case "Mostruário":
        return <td id={idColuna} className={estiloEstoque.totalTabela}>{globalMU.formatarValorNumerico(totaisEstoque.Mostruario, 3)}</td>;
      case "Reserva Mostruário":
        return <td id={idColuna} className={estiloEstoque.totalTabela}>{globalMU.formatarValorNumerico(totaisEstoque.ReservaMostruario, 3)}</td>;
      case "Quantidade RMA":
        return <td id={idColuna} className={estiloEstoque.totalTabela}>{globalMU.formatarValorNumerico(totaisEstoque.QuantidadeRMA, 3)}</td>;
      case "R":
        return <td id={idColuna} className={estiloEstoque.totalTabela} colSpan={2 + Number(!!props.mostrarColunaLocalFisico)}></td>;
      case "Encomenda":
        return <td id={idColuna} className={estiloEstoque.totalTabela}>{globalMU.formatarValorNumerico(totaisEstoque.Encomenda, 3)}</td>;
      default:
        return null;
    }
  }, [totaisEstoque, props.mostrarColunaLocalFisico]);

  const linhaTotalizador = useMemo(() => (
    <tr>
      {colunasSelecionadas.map((coluna) => (
        <React.Fragment key={coluna.Nome}>
          {renderizarCelulaTotalEstoque(coluna.Nome)}
        </React.Fragment>
      ))}
    </tr>
  ), [colunasSelecionadas, renderizarCelulaTotalEstoque]);

  return (
    <>

      {descricaoProdutoSelecionado}

      <AcoesHeadEstoque
        permiteEngrenagemGrid={props.permiteEngrenagemGrid}
        onToggleColunaSelecionada={props.onToggleColunaSelecionada}
        onAlterarFonte={props.onAlterarFonte}
        tamanhoFonte={props.tamanhoFontePadrao}
        colunas={colunasCabecalho}
        onToggleSelecionarTodasColunas={props.onToggleSelecionarTodasColunas}
      />

      <div
        style={{
          overflow: "auto",
          fontSize: fonteGradeEmPxOuPadrao(props.tamanhoFontePadrao),
        }}
      >
        <table
          id="Estoque_tabelaEstoque"
          title="Veja parâmetros: &#013;- Visualiza apenas filiais autorizadas na Consulta/Estoque&#013;&#013;- Exibir estoque de outras filiais na consulta de produtos&#013;- Desconto à vista para devoluções&#013;- Desconto para Venda a Vista&#013;- Exibir preço e comissão na consulta de produtos&#013;- Exibir somente produtos relacionados na área de vendas?&#013;- Permissão para visualização de imagens de produtos&#013;- Utiliza lista de oferta por área de vendas"
          className={`_table _w-full _border-collapse ${estiloEstoque.tabelaEstoque} `}
        >

          <HeadersEstoque colunas={colunasCabecalho} />

          <tbody
            id="Estoque_tabelaEstoqueBody"
          >
            {linhasDados}
            {linhaTotalizador}
          </tbody>
        </table>
      </div>

      <br />

      <Legenda
        itens={LEGENDA_ITENS_ESTOQUE}
      />
      <section style={{ marginBottom: "100px" }} />
    </>
  );
});
